import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useNavigate } from "react-router";
import { navigationItems, type NavigationItem } from "@/constants/index";
import { ArrowButton } from "@/components/ui/arrow-button";
import { scrollToElement } from "@/lib/scroll";

interface MobileNavProps {
  onNavigationClick: () => void; // Callback to close sidebar
}

// Module-level helper kept here (not extracted) since mobile-nav is the
// only consumer. Mirrors the cross-page hash logic in nav-dropdown.tsx
// and tab.tsx so all three nav surfaces behave identically.
function navigateToHref(
  href: string,
  navigate: ReturnType<typeof useNavigate>,
) {
  const hashIndex = href.indexOf("#");
  const path = hashIndex === -1 ? href : href.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : href.slice(hashIndex);

  // Cross-page hash link — defer scroll to <ScrollToHash />.
  if (path && path !== window.location.pathname) {
    navigate(path + hash);
    return;
  }

  // Same-page hash link — scroll directly.
  if (hash) {
    scrollToElement(hash.slice(1));
    return;
  }

  // Plain SPA route.
  if (href.startsWith("/")) navigate(href);
}

export function MobileNav({ onNavigationClick }: MobileNavProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleTopClick = (item: NavigationItem) => {
    if (item.dropdown) {
      setExpanded((cur) => (cur === item.name ? null : item.name));
      return;
    }
    navigateToHref(item.mobileLink || item.link, navigate);
    onNavigationClick();
  };

  const handleSubClick = (href: string, external?: boolean) => {
    if (external) {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    navigateToHref(href, navigate);
    onNavigationClick();
  };

  return (
    <nav className="flex flex-col gap-1 flex-1 p-4">
      {navigationItems.map((item) => {
        const isExpanded = expanded === item.name;
        return (
          <div key={item.name} className="border-b border-border last:border-b-0">
            <button
              onClick={() => handleTopClick(item)}
              className="w-full flex items-center justify-between gap-3 py-3 text-xl font-heading text-foreground rounded-lg transition-all hover:bg-accent/50 text-left cursor-pointer"
            >
              <span>{item.name}</span>
              {item.dropdown && (
                <ChevronDown
                  className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${isExpanded ? "rotate-180" : ""
                    }`}
                />
              )}
            </button>

            {item.dropdown && isExpanded && (
              <ul className="pb-3 pl-2 space-y-1">
                {item.dropdown.groups.flatMap((group, gi) => {
                  const rows: React.ReactNode[] = [];
                  if (group.title) {
                    rows.push(
                      <li
                        key={`${item.name}-${gi}-title`}
                        className="px-3 pt-2 pb-1 font-body text-[10px] uppercase tracking-widest text-muted-foreground"
                      >
                        {group.title}
                      </li>
                    );
                  }
                  for (const entry of group.items) {
                    const Icon = entry.icon;
                    rows.push(
                      <li key={`${item.name}-${entry.label}`}>
                        <button
                          onClick={() => handleSubClick(entry.href, entry.external)}
                          className="w-full flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-accent/40 transition-colors text-left cursor-pointer"
                        >
                          <Icon className="w-4 h-4 mt-0.5 text-foreground/70 shrink-0" />
                          <span>
                            <span className="block font-body text-sm font-medium text-foreground">
                              {entry.label}
                            </span>
                            <span className="block font-body text-xs text-muted-foreground font-light">
                              {entry.description}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  }
                  return rows;
                })}
              </ul>
            )}
          </div>
        );
      })}

      {/* Login Button */}
      <div className="mt-6 w-full">
        <a
          href="https://poly.agencyofpoly.com"
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full"
        >
          <ArrowButton>login</ArrowButton>
        </a>
      </div>
    </nav>
  );
}
